import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/vue-query';
import { until } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { request } from '@/api/request';
import {
  mergeMutationOptions,
  mergeQueryOptions,
  retryPageReadRequirement,
  retryIdempotentWrite,
  type QueryOptions,
  type MutationOptions,
} from '@/modules/query/utils';
import { isAuthenticatedByRLRef } from '@/modules/auth/state';
import { TRIAL_QUERY } from '@/modules/trial/api/queryKeys';
import type { Engagement, EngagementType } from '@@/packages/shared/types/models/engagements';
import type { Organisation } from '@@/packages/shared/types/models/organisation';
import type { UserBasicInfo } from '@@/packages/shared/types/models/user';
import { ORG_ENGAGEMENTS_QUERY, ORG_QUERY, ORG_USERS_QUERY } from './queryKeys';

type UpdateOrganisationVariables = unknown;

export type UsersResponse = {
  usersByID: Record<string, UserBasicInfo>;
};

async function fetchOrg() {
  const res = await request<Organisation>('GET', '/org');
  return res.data;
}

// Imperatively (re)fetch the org into the cache. Used by the org soft reload,
// where the app shell is unmounted behind the loading screen, so no observer is
// alive to refetch after the cache is cleared.
export function fetchCurrentOrg(queryClient: QueryClient) {
  return queryClient.fetchQuery({ queryKey: [ORG_QUERY], queryFn: fetchOrg });
}

export function useCurrentOrg(options?: QueryOptions) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [ORG_QUERY],
      queryFn: fetchOrg,
      staleTime: Infinity,
      enabled: isAuthenticatedByRLRef,
      ...retryPageReadRequirement(),
    }),
  );

  const currentOrg = computed(() => query.data.value);

  async function resolvedOrg() {
    if (!isAuthenticatedByRLRef.value) return undefined;
    if (!currentOrg.value) {
      await until(currentOrg).toMatch((c) => !!c);
    }
    if (!currentOrg.value) throw new Error('this is impossible');
    return currentOrg.value;
  }

  /**
   * Waits for organisation to be defined and returns a boolean value.
   */
  const isClient = async () => (await resolvedOrg())?.isClient;
  const isSupplier = async () => (await resolvedOrg())?.isSupplier;
  const isFederated = async () => (await resolvedOrg())?.isFederated;
  const isClientRestricted = async () => (await resolvedOrg())?.isClientRestricted;
  const isRL = computed(() => currentOrg.value?.orgID === import.meta.env.VUE_APP_RL_ORG_ID);

  return {
    ...query,
    query,
    currentOrg,
    resolvedOrg,
    isClient,
    isSupplier,
    isFederated,
    isClientRestricted,
    isRL,
  };
}

export function useUpdateCurrentOrg(
  options?: MutationOptions<Organisation, UpdateOrganisationVariables>,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn(user) {
        const res = await request('PATCH', '/org', user);
        return res.data;
      },
      onSuccess(data) {
        queryClient.setQueryData([ORG_QUERY], data);
      },
    }),
  );
}

export function useBecomeSupplier(options?: MutationOptions<void, void>) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn() {
        await request('POST', '/org/supplier');
      },
      // Safe to retry: the endpoint no-ops when the org is already a supplier,
      // and the org update and its event are in one transaction. Without this a
      // single 5xx put a dead "Failed to set organisation type" banner in front
      // of a new user.
      ...retryIdempotentWrite(),
      onSuccess() {
        queryClient.setQueryData([ORG_QUERY], (org: Organisation) => ({
          ...org,
          isSupplier: true,
        }));
      },
    }),
  );
}

export function useBecomeClient(options?: MutationOptions<void, void>) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn() {
        await request('POST', '/org/client');
      },
      // Safe to retry: the endpoint returns early once the org is a client, and
      // only initiates a trial when there isn't an active one. See
      // useBecomeSupplier above.
      ...retryIdempotentWrite(),
      async onSuccess() {
        queryClient.setQueryData([ORG_QUERY], (org: Organisation) => ({
          ...org,
          isClient: true,
          clientAt: new Date().toISOString(),
        }));
        await queryClient.invalidateQueries({ queryKey: [TRIAL_QUERY] });
      },
    }),
  );
}

/**
 * Orders users the way selection lists and filters should show them: the
 * current user first, then everyone else alphabetically by full name. The
 * `/users` response is a map keyed by user ID, so without this the order is
 * whatever the IDs happen to sort as.
 *
 * Pinning is positional only. Callers filter the set they pass in, so a current
 * user who has been excluded (already an approver, lacking a permission) is not
 * reintroduced by the sort.
 */
export function sortOrgUsers(users: UserBasicInfo[], currentUserID?: string) {
  return users.toSorted((userA, userB) => {
    if (userA.id === currentUserID) return -1;
    if (userB.id === currentUserID) return 1;
    return userA.fullName.localeCompare(userB.fullName);
  });
}

export function useOrgUsers(options?: QueryOptions) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [ORG_USERS_QUERY],
      async queryFn() {
        const res = await request<UsersResponse>('GET', '/users');
        return res.data.usersByID;
      },
      enabled: isAuthenticatedByRLRef,
    }),
  );

  // The endpoint returns every ever-member tagged with `membershipState`.
  // `allOrgUsers` filters to active members only — the default for selection
  // lists, filters and "Users and roles", which must not show deactivated users.
  const allOrgUsersIncludingDeactivated = computed(() => query.data.value || {});
  const allOrgUsers = computed(() =>
    Object.fromEntries(
      Object.entries(allOrgUsersIncludingDeactivated.value).filter(
        ([, user]) => user.membershipState !== 'removed',
      ),
    ),
  );

  const { t } = useI18n();

  function isUserDeactivated(userID: string | null | undefined) {
    return !!userID && allOrgUsersIncludingDeactivated.value[userID]?.membershipState === 'removed';
  }

  // Resolves against the full set so historical surfaces can render a removed
  // user's name, suffixed with `(deactivated)`. Unknown IDs fall back to the
  // deleted-user label.
  function getUserName(userID: string | null | undefined) {
    // Important to check that userID is defined, or if the env var is undefined
    if (userID && userID === import.meta.env.VUE_APP_SYSTEM_USER) {
      return t('global.systemUser');
    }
    const fullName = userID && allOrgUsersIncludingDeactivated.value[userID]?.fullName;
    if (!fullName) return t('global.deletedUser');
    return isUserDeactivated(userID) ? t('global.deactivatedUser', { name: fullName }) : fullName;
  }

  return {
    ...query,
    allOrgUsers,
    allOrgUsersIncludingDeactivated,
    getUserName,
    isUserDeactivated,
  };
}

export function useOrgEngagements(options?: QueryOptions) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [ORG_ENGAGEMENTS_QUERY],
      async queryFn() {
        const res = await request<Engagement[]>('GET', '/org/engagements');
        return res.data;
      },
      enabled: isAuthenticatedByRLRef,
    }),
  );

  function isOrgEngagementAboveThreshold(engagementType: EngagementType, threshold: number) {
    const engagement = query.data.value?.find((e) => e.engagementType === engagementType);
    return engagement && engagement.engagementCount > threshold;
  }

  function isOneOfOrgEngagementAboveThreshold(
    engagementTypes: EngagementType[],
    threshold: number,
  ) {
    return query.data.value?.some(
      (engagement) =>
        engagementTypes.includes(engagement.engagementType) &&
        engagement.engagementCount > threshold,
    );
  }

  return { isOrgEngagementAboveThreshold, isOneOfOrgEngagementAboveThreshold, ...query };
}
